import {trpc} from '@common-web/lib/trpc';
import {Card, CardContent, CardHeader, CardTitle} from '@common-web/components/ui/card';
import {DataTable, type Column} from '@common-web/components/data-table';
import type {RouterOutput} from '@common-web/lib/trpc';
import {
  ActiveFilterPills,
  ConsolidatedFilterBar,
} from '@internal/pages/claims/ConsolidatedFilterBar';
import {useConsolidatedFilters} from '@internal/pages/claims/useConsolidatedFilters';

type Row = RouterOutput['internal']['claimsTracking']['consolidated']['getGroupsByExCode'][number];

export function GroupRollupTab({onSelectGroup}: {onSelectGroup: (groupId: string) => void}) {
  const {filters} = useConsolidatedFilters();

  const query = trpc.internal.claimsTracking.consolidated.getGroupsByExCode.useQuery(filters);

  const columns: Column<Row>[] = [
    {
      id: 'groupName',
      header: 'Group',
      render: (row) => (
        <button
          type='button'
          onClick={() => row.groupId && onSelectGroup(row.groupId)}
          disabled={!row.groupId}
          className='text-left text-sm text-brand-navy hover:text-primary hover:underline disabled:no-underline disabled:cursor-default'
        >
          {row.groupName ?? row.groupId ?? '(No Group ID)'}
        </button>
      ),
    },
    {
      id: 'distinctClaims',
      header: 'Distinct claims',
      align: 'right',
      render: (row) => (
        <span className='font-mono tabular-nums text-sm'>
          {row.distinctClaims.toLocaleString()}
        </span>
      ),
    },
    {
      id: 'exCodeCount',
      header: 'Open ex codes',
      align: 'right',
      render: (row) => (
        <span className='font-mono tabular-nums text-sm text-muted-foreground'>
          {row.exCodeCount.toLocaleString()}
        </span>
      ),
    },
  ];

  return (
    <div className='space-y-4'>
      <Card>
        <CardContent className='pt-6 space-y-3'>
          <div className='flex items-end gap-3 flex-wrap'>
            <ConsolidatedFilterBar />
          </div>
          <ActiveFilterPills />
        </CardContent>
      </Card>

      <Card>
        <CardHeader className='pb-3'>
          <CardTitle className='text-base'>Groups by open ex codes</CardTitle>
        </CardHeader>
        <CardContent>
          <p className='text-sm text-muted-foreground mb-4'>
            Groups ranked by distinct claims carrying uncleared ex codes. Click a group to see its
            claims.
          </p>
          <DataTable<Row>
            columns={columns}
            data={query.data}
            loading={query.isLoading}
            error={query.error}
            emptyMessage='No groups for these filters.'
            rowKey={(row) => row.groupId ?? row.groupName ?? '(none)'}
          />
        </CardContent>
      </Card>
    </div>
  );
}
